import React, { useState, useRef, useEffect } from "react";
import { StateLine, effState } from "./Card";
import { panelOf, allowed, actionOf, topicLine, linkOf } from "./model";
import { fmtDay } from "../../lib/vikas";

export const TEAM_ROW = [["New date", "new date", "date"], ["Close it", "close"], ["Escalate", "escalate"], ["Later", "later"]];
export const MARK_ROW = [["Stale", "stale"], ["Not relevant", "not relevant"], ["VIKAS mistake", "vikas mistake"]];

function Part({ n, label, cls = "", children, testid }) {
  return (
    <section className={`pp ${cls}`} data-testid={testid}>
      <div className="label"><i>{n}</i>{label}</div>
      {children}
    </section>
  );
}

const isOpt = (c, a) => (c.options || []).some((o) => String(o).trim().toLowerCase() === String(a).trim().toLowerCase());

export function MoveRow({ title, items, c, ctx, lockedOut, testid }) {
  const [dateFor, setDateFor] = useState(null);
  const [val, setVal] = useState("");
  const pick = useRef(null);
  const panel = panelOf(c);
  useEffect(() => {
    if (!dateFor || !pick.current) return;
    pick.current.focus();
    try { pick.current.showPicker?.(); } catch (e) { /* picker needs a user gesture on some browsers */ }
  }, [dateFor]);
  const openPicker = (action) => { setVal(""); setDateFor(dateFor ? null : action); };
  if (!items.length) return null;
  return (
    <div className="moves" data-testid={testid}>
      <span className="rl">{title}</span>
      {items.map(([label, action, mode], i) => {
        const ok = ctx.canAct && ctx.canPress(c, action) && !lockedOut && (allowed(panel, action) || ctx.ownPress(c, action) || isOpt(c, action));
        return (
          <button key={label} className={`btn${i === 0 && title === "The decision" ? " primary" : ""}`} disabled={!ok}
            data-testid={`move-${action.replace(/\s+/g, "-")}`}
            onClick={() => (mode === "date" ? openPicker(action) : ctx.act(panel, action, c.id, "", c.topic || ""))}>
            {label}
          </button>
        );
      })}
      {dateFor && (
        <div className="moves-extra">
          <input type="date" ref={pick} className="vinput" value={val} onChange={(e) => setVal(e.target.value)} aria-label="Pick the new date" data-testid="move-date-input" />
          <button className="btn primary" disabled={!ISO.test(val)} data-testid="move-date-confirm"
            onClick={() => { if (!ISO.test(val)) return; ctx.act(panel, dateFor, c.id, val, c.topic || ""); setDateFor(null); setVal(""); }}>Queue</button>
          <button className="btn ghost" data-testid="move-date-cancel" onClick={() => { setDateFor(null); setVal(""); }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

function GroupRows({ c, onRow }) {
  const rows = Array.isArray(c.items) ? c.items : [];
  if (!rows.length) return <p className="faint small">No rows yet</p>;
  return (
    <ul className="grows" data-testid="group-rows">
      {rows.map((r) => (
        <li key={r.id}>
          <button className="grow" data-testid={`group-row-${r.id}`} onClick={() => onRow(r.id)}>
            <span className="gid">{r.id}</span> <span className="gt">{r.title}</span>{r.sub ? <span className="gs"> · {r.sub}</span> : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

function NoteBox({ c, ctx }) {
  const [t, setT] = useState("");
  return (
    <div className="notebox">
      <input className="vinput" placeholder="Add a note for the team" value={t} disabled={!(ctx.canAct && ctx.canPress(c, "note"))}
        onChange={(e) => setT(e.target.value)} data-testid="panel-note-input" />
      <button className="btn" disabled={!(ctx.canAct && ctx.canPress(c, "note")) || !t.trim()} data-testid="panel-note-add"
        onClick={() => { ctx.act(panelOf(c), "note", c.id, t.trim(), c.topic || ""); setT(""); }}>Add note</button>
    </div>
  );
}

function FileLine({ f, testid }) {
  const name = f.name || f.id || "file";
  return (
    <div className="files" data-testid={testid}>
      <span className="chip">{name}</span>
      {f.url && <a className="btn ghost" href={f.url} target="_blank" rel="noreferrer">Open</a>}
      {f.at && <span className="faint">{f.at}</span>}
    </div>
  );
}

export function TeamOnlyPanel({ c, press, ctx, onClose }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus({ preventScroll: true }); }, [c.id]);
  const [st, words] = effState(c, press);
  const moves = (c.actions || []).map((l) => [l, actionOf(l), actionOf(l) === "new date" ? "date" : undefined]);
  return (
    <aside className="panel" data-testid="card-panel" aria-label={c.title} tabIndex={-1} ref={ref}>
      <div className={`ph ${st}`}>
        <button className="x" aria-label="Close the panel" data-testid="panel-close" onClick={onClose}>×</button>
        <h2 data-testid="panel-title">{c.title}</h2>
        <StateLine state={st} words={words} testid="panel-state" />
        {c.due && <div className="chips"><span className="chip">{c.due}</span></div>}
      </div>
      <div className="pbody">
        {ctx.teamPress(c) && moves.length > 0 && (
          <Part n={1} label="Your move" cls="move" testid="panel-part-3">
            <MoveRow title="Team actions" items={moves} c={c} ctx={ctx} testid="moves-team" />
          </Part>
        )}
      </div>
    </aside>
  );
}

export function CardPanel({ c, press, ctx, onClose, onPdf, onRow, back }) {
  const isGroup = Boolean(c.group);
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus({ preventScroll: true }); }, [c.id]);
  const [st, words] = effState(c, press);
  const decision = Array.isArray(c.options) && c.options.length ? c.options.map((l) => [l, l]) : (c.actions || []).map((l) => [l, actionOf(l)]);
  const lockedOut = c.state === "locked" && ctx.role !== "Saket";
  const hist = (c.history || []).slice(0, 8);
  const logs = (c.files || []).filter((f) => /log/i.test(`${f.kind || ""} ${f.name || ""}`));
  const chips = [c.owner ? `Owner · ${c.owner}` : "Owner: not set", c.due, c.age, c.impact_full && `Impact · ${c.impact_full}`].filter(Boolean);
  return (
    <aside className="panel" data-testid="card-panel" aria-label={c.title} tabIndex={-1} ref={ref}>
      <div className={`ph ${st}`}>
        <button className="x" aria-label="Close the panel" data-testid="panel-close" onClick={onClose}>×</button>
        {back && <button className="btn ghost tiny back-group" data-testid="panel-back-group" onClick={back}>‹ Back to the group</button>}
        <div className="label" data-testid="panel-head-label">{topicLine(c)}{c.id ? ` · ${c.id}` : ""}</div>
        <h2 data-testid="panel-title">{c.title}</h2>
        <StateLine state={st} words={words} testid="panel-state" />
      </div>
      <div className="pbody">
        <Part n={1} label="What and why" testid="panel-part-1">{c.what ? <p>{c.what}</p> : <p className="faint small">Nothing written yet</p>}</Part>
        <Part n={2} label="Where it stands" testid="panel-part-2">
          {c.stands ? <p>{c.stands}</p> : null}
          {!c.stands && !c.next && chips.length === 0 && <p className="faint small">Nothing written yet</p>}
          {c.next ? <p><b className="nx">Next:</b> {c.next}</p> : null}
          {chips.length > 0 && <div className="chips">{chips.map((x) => <span key={x} className="chip">{x}</span>)}</div>}
        </Part>
        {isGroup ? (
          <Part n={3} label="The rows" testid="panel-part-rows">
            <p className="small" data-testid="group-move-line">Open a row below to close it, move its date or add a note.</p>
            <GroupRows c={c} onRow={onRow} />
          </Part>
        ) : (
        <Part n={3} label="Your move" cls="move" testid="panel-part-3">
          <MoveRow title="The decision" items={decision} c={c} ctx={ctx} lockedOut={lockedOut} testid="moves-decision" />
          <MoveRow title="Team actions" items={TEAM_ROW} c={c} ctx={ctx} testid="moves-team" />
          <MoveRow title="Mark this card" items={MARK_ROW} c={c} ctx={ctx} testid="moves-mark" />
          <div className="faint small">A press is logged with your name and the time.</div>
        </Part>
        )}
        <Part n={4} label="History" testid="panel-part-4">
          {hist.length === 0 && <p className="faint small" data-testid="panel-history-empty">No history yet</p>}
          <ul className="hist">{hist.map((h, i) => <li key={i}><span>{h.date}</span><span>{h.text}</span></li>)}</ul>
          {c.history_older > 0 && <div className="faint small" data-testid="panel-history-older">+{c.history_older} older</div>}
        </Part>
        <Part n={5} label="Files" testid="panel-part-5">
          {!c.file && !(c.files || []).length && !(c.uploads || []).length && <p className="faint small" data-testid="panel-files-empty">No files yet</p>}
          {c.file && <div className="files"><span className="chip">{c.file.name}</span><button className="btn ghost" data-testid="panel-open-pdf" onClick={() => onPdf(c)}>Open PDF</button></div>}
          {(c.files || []).map((f, i) => <FileLine key={`f${i}`} f={f} testid={`panel-file-${i}`} />)}
          {(c.uploads || []).map((f, i) => <FileLine key={`u${i}`} f={f} testid={`panel-upload-${i}`} />)}
        </Part>
        <Part n={6} label="Notes and chat" testid="panel-part-6">
          <NoteBox c={c} ctx={ctx} />
          {ctx.askVikas && <button className="btn ghost ask-vikas" data-testid="panel-ask-vikas" onClick={() => ctx.askVikas(c.id)}>Ask VIKAS about this card</button>}
        </Part>
        <Part n={7} label="Source and ids" testid="panel-part-7">
          <div className="ids">
            <span>Topic</span><span>{topicLine(c)}</span>
            <span>Card link</span><span data-testid="panel-card-link">{linkOf(c)}</span>
            {c.source?.name && <><span>Register</span><span>{c.source.url ? <a href={c.source.url} target="_blank" rel="noreferrer">{c.source.name}</a> : c.source.name}</span></>}
            {logs.length > 0 && <><span>Newest log</span><span>{logs[logs.length - 1].name}</span></>}
            <span>Id</span><span>{c.id}</span>
            {c.closed_on && <><span>Closed</span><span>{fmtDay(c.closed_on)}</span></>}
          </div>
        </Part>
      </div>
    </aside>
  );
}
